import { Compass, BookOpenCheck, HeartHandshake, ArrowUp, Phone } from 'lucide-react';
import { siteConfig } from '@/lib/siteConfig';

/**
 * LpWhyKGOB — Closing trust section below the form on each LP.
 *
 * Voice pulled directly from /pages/OurStory.js so PPC visitors hear the same
 * voice they'd hear if they explored the rest of kgob.com:
 *   • "calm, expert team" / "financial nerve center"
 *   • "planners, not just processors"
 *   • "respected and heard, not rushed or talked down to"
 *
 * Purpose:
 *   • Closing message for visitors who scroll past the form without converting
 *     — they're prime retargeting candidates via GA4 audiences
 *   • Gives them ONE last clear action (scroll back to form OR call)
 *
 * Brand: dark navy band (mirrors hero) so the page is symmetrically bookended.
 */

const PHONE_DIAL = siteConfig.phone.replace(/\D/g, '').replace(/^/, '+'); // +18445993355
const PHONE_DISPLAY = siteConfig.phoneDisplay;                            // 1-844-599-3355

const PILLARS = [
  {
    icon: Compass,
    title: "Planners, not processors",
    body: "Year-round strategy, not last-minute filings. We help you move from putting out fires to operating with a thoughtful, forward-looking plan.",
  },
  {
    icon: BookOpenCheck,
    title: "Real CPAs, no hand-offs",
    body: "Tax, accounting, and advisory under one team — led by Sara F. Gonzalez, CPA. You get one accountable point of contact, not a different person every call.",
  },
  {
    icon: HeartHandshake,
    title: "Calm, clear, respectful",
    body: "We make complex numbers understandable. Clients feel respected and heard — not rushed or talked down to. Bring us your \"messy.\" We'll bring structure and calm.",
  },
];

export const LpWhyKGOB = () => {
  const scrollToForm = (e) => {
    e.preventDefault();
    const el = document.getElementById("lp-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-[#0B2240] via-[#102a4d] to-[#0B2240] text-white py-14 sm:py-20"
      data-testid="lp-why-kgob"
    >
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 75% 30%, #C9A961 0%, transparent 45%), radial-gradient(circle at 20% 80%, #C9A961 0%, transparent 45%)",
        }}
      />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#C9A961]" />
            <span className="font-['Inter'] text-[11px] font-semibold tracking-[0.22em] uppercase text-[#C9A961]">
              Why KGOB
            </span>
            <span className="h-px w-8 bg-[#C9A961]" />
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold leading-tight max-w-2xl mx-auto">
            A calm, expert team you can lean on year-round.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-white/70 max-w-2xl mx-auto">
            Since 1988, KGOB has been the firm growth-minded clients turn to when they want a CPA who answers the phone — and actually plans ahead.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-10 sm:mb-12">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                className="rounded-xl bg-white/[0.04] border border-[#C9A961]/20 p-5 sm:p-6"
                data-testid={`lp-why-pillar-${i}`}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#C9A961]/15 mb-3.5">
                  <Icon className="h-5 w-5 text-[#C9A961]" strokeWidth={1.8} />
                </span>
                <h3 className="font-['Playfair_Display'] text-lg sm:text-xl font-semibold mb-2 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-white/75 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>

        {/* Closing CTA pair */}
        <div className="text-center" data-testid="lp-why-cta-block">
          <p className="text-sm sm:text-base text-white/80 mb-4">
            When you&apos;re ready, we&apos;re here.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a
              href="#lp-form"
              onClick={scrollToForm}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A961] hover:bg-[#D4B975] text-[#0B2240] font-semibold px-6 py-3 text-sm sm:text-base transition min-h-[48px]"
              data-testid="lp-why-form-cta"
            >
              <ArrowUp className="h-4 w-4" />
              Send us your details
            </a>
            <a
              href={`tel:${PHONE_DIAL}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 hover:bg-white/15 text-white font-medium px-5 py-3 text-sm sm:text-base transition min-h-[48px]"
              data-testid="lp-why-phone-cta"
            >
              <Phone className="h-4 w-4 text-[#E8D49A]" />
              Or call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
